import { useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import { gql } from "../../../../__generated__";
import type {
  Channel,
  MissionAdminCampaignFilter,
} from "../../../../__generated__/graphql";

interface FilterState {
  channel: Channel | null;
  brandName: string;
  productName: string;
  hasUpcomingRun: boolean | null;
}

const MISSION_ADMIN_CAMPAIGNS_FOR_LIST = gql(/* GraphQL */ `
  query MissionAdminCampaignsForList($filter: MissionAdminCampaignFilter) {
    missionAdminCampaigns(filter: $filter) {
      id
      brand {
        name
      }
      productName
      productImageUrl
      channel
      originalPrice
      discountPrice
      paybackPrice
      isPublished
      runs {
        id
        startAt
        endAt
        isActive
      }
      createdAt
    }
  }
`);

const MissionAdminCampaignsList = () => {
  const [filter, setFilter] = useState<FilterState>({
    channel: null,
    brandName: "",
    productName: "",
    hasUpcomingRun: null,
  });

  const { data, loading, error, refetch } = useQuery(
    MISSION_ADMIN_CAMPAIGNS_FOR_LIST,
    { variables: { filter: {} } }
  );

  const handleFilterChange = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const filterInput: MissionAdminCampaignFilter = {};
    if (filter.channel) filterInput.channel = filter.channel;
    if (filter.brandName) filterInput.brandName = filter.brandName;
    if (filter.productName) filterInput.productName = filter.productName;
    if (filter.hasUpcomingRun !== null)
      filterInput.hasUpcomingRun = filter.hasUpcomingRun;

    refetch({ filter: filterInput });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const formatMonthDay = (date: unknown) => {
    const parsed = date instanceof Date ? date : new Date(String(date));
    if (Number.isNaN(parsed.getTime())) return "-";
    return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
  };

  const isPastRun = (startAt: unknown) => {
    const startTime = new Date(String(startAt)).getTime();
    if (Number.isNaN(startTime)) return false;
    return startTime < Date.now();
  };

  const getChannelLabel = (channel: Channel) => {
    const labels: Record<Channel, string> = {
      NAVER: "네이버",
      COUPANG: "쿠팡",
      CARROT: "당근",
    };
    return labels[channel] ?? channel;
  };

  if (loading) return <div>로딩 중...</div>;
  if (error)
    return <div>에러: {error instanceof Error ? error.message : ""}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">캠페인 목록</h1>
        <Link
          to="/ops/mission/campaigns/create"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          캠페인 생성
        </Link>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              채널
            </label>
            <select
              value={filter.channel || ""}
              onChange={(e) =>
                handleFilterChange(
                  "channel",
                  (e.target.value || null) as Channel | null
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">전체</option>
              <option value="NAVER">네이버</option>
              <option value="COUPANG">쿠팡</option>
              <option value="CARROT">당근</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              브랜드명
            </label>
            <input
              type="text"
              value={filter.brandName}
              onChange={(e) => handleFilterChange("brandName", e.target.value)}
              placeholder="브랜드명 검색"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              제품명
            </label>
            <input
              type="text"
              value={filter.productName}
              onChange={(e) =>
                handleFilterChange("productName", e.target.value)
              }
              placeholder="제품명 검색"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={applyFilters}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              필터 적용
            </button>
          </div>
        </div>
      </div>

      {/* 캠페인 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                제품 사진
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                브랜드/제품
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                채널
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                가격 정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                회차 정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.missionAdminCampaigns?.map((campaign) => (
              <tr key={campaign.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  {campaign.productImageUrl ? (
                    <ResponsiveCDNImage
                      src={campaign.productImageUrl}
                      alt={campaign.productName}
                      className="w-16 h-16 object-cover rounded-lg"
                      sizeRatio={0.25}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-400">이미지 없음</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {campaign.brand.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {campaign.productName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {getChannelLabel(campaign.channel)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>정가: {formatPrice(campaign.originalPrice)}원</div>
                  <div>할인가: {formatPrice(campaign.discountPrice)}원</div>
                  <div className="font-semibold text-blue-600">
                    페이백: {formatPrice(campaign.paybackPrice)}원
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {campaign.runs.length ? (
                    <div className="flex flex-wrap gap-2">
                      {[...campaign.runs]
                        .sort((a, b) => {
                          const aTime = new Date(String(a.startAt)).getTime();
                          const bTime = new Date(String(b.startAt)).getTime();

                          // 시간순(오래된 -> 최신) 정렬
                          const aInvalid = Number.isNaN(aTime);
                          const bInvalid = Number.isNaN(bTime);
                          if (aInvalid && bInvalid) return 0;
                          if (aInvalid) return 1;
                          if (bInvalid) return -1;
                          return aTime - bTime;
                        })
                        .map((run) => {
                          const isPast = isPastRun(run.startAt);
                          const isActiveUpcoming = run.isActive && !isPast;
                          const helperText = isPast
                            ? "시간지남"
                            : !run.isActive
                            ? "비활성화"
                            : null;

                          return (
                            <div
                              key={run.id}
                              className="flex flex-col items-center"
                            >
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  isActiveUpcoming
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-700 opacity-80"
                                }`}
                              >
                                {formatMonthDay(run.startAt)}
                              </span>
                              {helperText && (
                                <span className="mt-1 text-[10px] leading-none text-gray-500">
                                  {helperText}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    "예정 없음"
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      campaign.isPublished
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {campaign.isPublished ? "공개" : "비공개"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <Link
                    to={`/ops/mission/campaigns/${campaign.id}`}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    상세보기
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!data?.missionAdminCampaigns?.length && (
          <div className="text-center py-12 text-gray-500">
            등록된 캠페인이 없습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionAdminCampaignsList;
